import React from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { formatFlightDistance, formatFlightTime } from '@src/core/extensions';
import { useUnitType } from '@src/core/features/settings';
import { useGetDroneMaintenanceQuery } from '@src/features/fleet/redux/api';
import type { IGetDroneMaintenanceResponse } from '@src/features/fleet/redux/types';
import { isInitialLoad } from '@src/features/fleet/pages/drones-page/utils/query-state';
import type { FlightStats } from '../types';

const EMPTY_STATS: FlightStats = { count: '—', time: '—', distance: '—', isLoading: false };

function toFlightStats(maintenance: IGetDroneMaintenanceResponse | undefined): FlightStats {
  if (!maintenance) return EMPTY_STATS;

  return {
    count: maintenance.totalFlightCount,
    time: formatFlightTime(maintenance.totalFlightTime),
    distance: formatFlightDistance(maintenance.totalFlightDistance),
    isLoading: false,
  };
}

export function useDroneFlightStats(droneId: number | undefined, enabled: boolean): FlightStats {
  const unitType = useUnitType();
  const {
    currentData: maintenance,
    isLoading,
    isFetching,
  } = useGetDroneMaintenanceQuery(enabled && droneId != null ? droneId : skipToken);
  const isStatsLoading = isInitialLoad({ isLoading, isFetching }, maintenance);

  return React.useMemo(() => ({
    ...toFlightStats(maintenance),
    isLoading: isStatsLoading,
  }), [isStatsLoading, maintenance, unitType]);
}
